// src/docs/api/groups/authEndpoints.ts
import type { ApiEndpoint } from "../apiDocsSchema";

export const authEndpoints: ApiEndpoint[] = [
  {
    group: "Auth (Acesso ao Sistema)",
    name: "Realizar Login",
    method: "POST",
    path: "/auth/login",
    authRequired: false,
    usageDescription:
      "Essa API é utilizada em https://${seudominio}.com/entrar, sendo essencial para acesso ao sistema.",

    responseDetails:
      "Retorna no primeiro json dados referente ao usuário, em config (departamentos_ativos) é retornado quais departamentos o usuário atual pode acessar e/ou consultar, em theme é retornado configurações referente ao tema do sistema como cores principais normalmente semelhante ao padrão cromático da empresa contratante, em systemTitle é retornado o nome a ser utilizado no sistema.",
    description:
      "Autentica o usuário para acesso ao sistema. É necessário que o cadastro tenha sido previamente efetuado e aprovado por um administrador.",
    params: [
      {
        name: "email",
        in: "body",
        type: "string",
        required: true,
        description:
          "E-mail utilizado para acesso ao sistema. Deve corresponder a um cadastro previamente aprovado.",
        example: "fulano@example.com",
        source:
          "É o mesmo e-mail informado na etapa de cadastro do usuário e aprovado pelo administrador do sistema.",
      },
      {
        name: "senha",
        in: "body",
        type: "string",
        required: true,
        description: "Senha utilizada para autenticação do usuário no sistema.",
        example: "SenhaForte123",
        source:
          "Senha definida pelo usuário no momento do cadastro. Deve ser informada exatamente como foi cadastrada.",
      },
      {
        name: "remember",
        in: "body",
        type: "boolean",
        required: true,
        description:
          "Indica se o login deve ser mantido para o próximo acesso. Se for false, será necessário realizar login novamente no próximo acesso.",
        example: true,
        source:
          "Definido pela aplicação cliente (frontend / integração). Use true para manter sessão por mais tempo, ou false para exigir login a cada nova sessão.",
      },
      {
        name: "latitude",
        in: "body",
        type: "number",
        required: false,
        description:
          "Latitude aproximada da localização do usuário no momento do login. Usada para auditoria, relatórios de operação e mecanismos de segurança (por exemplo, detecção de acessos em locais inesperados).",
        example: -22.898454,
        source:
          "Normalmente coletada pela aplicação cliente via navigator.geolocation (navegador) ou serviço equivalente no dispositivo, mediante permissão do usuário.",
      },
      {
        name: "longitude",
        in: "body",
        type: "number",
        required: false,
        description:
          "Longitude aproximada da localização do usuário no momento do login. Complementa a latitude para permitir identificação de localização geográfica no ato do acesso.",
        example: -43.111818,
        source:
          "Normalmente coletada pela aplicação cliente via navigator.geolocation (navegador) ou serviço equivalente no dispositivo, mediante permissão do usuário.",
      },
    ],
    playgroundDefaults: {
      body: {
        email: "fulano@example.com",
        senha: "SenhaForte123",
        remember: true,
        latitude: -22.898454,
        longitude: -43.111818,
      },
    },
    examples: [
      {
        title: "Exemplo de resposta (200)",
        response: {
          ok: true,
          usuario: {
            id: 13,
            nome: "Fulano de Tal",
            email: "fulano@example.com",
            departamento: "desenvolvedor",
            funcao: "Suprimentos",
            ativo: true,
            empresa_id: "2",
            picture: "01010101",
          },
          config: {
            departamentos_ativos: ["suprimentos", "laboratorio", "diretoria"],
            theme: {
              hash: "havox-dark-1",
              line: "#445816FF",
              third: "#202124",
              primary: "#1f2908",
              secondary: "#F9AB00",
              primary_button: "#547110FF",
              secondary_button: "#6c9114",
              table_option_hover: "#3770240B",
              table_option_selected: "#3770247C",
              menu_buttons_selection: "#334311",
              menu_buttons_selection_effect: "#88b629",
            },
            systemTitle: ["HAVOX"],
          },
          accessToken:
            "ba721b29788ejf5a93c2c0029a3847c31h2c2a2ef67bee7a708fc0ad653f67a",
          expiresIn: 43200,
          scopes: ["system:user"],
        },
      },
    ],
  },
  {
    group: "Auth (Acesso ao Sistema)",
    name: "Realizar Cadastro",
    method: "POST",
    path: "/auth/register",
    authRequired: false,
    usageDescription:
      "Essa API é utilizada em https://${seudominio}.com/cadastro, sendo essencial para registro do usuário e vinculo de suas respectivas apiKeys.",

    responseDetails:
      "Retorna o objeto completo do usuário criado, e o nome vinculado a respectiva apiKey.",

    description: "Cria um novo usuário no sistema e retorna o accessToken.",
    developed: true,

    params: [
      {
        name: "nome",
        in: "body",
        type: "string",
        required: true,
        description: "Nome completo do usuário que será cadastrado.",
        example: "Fulano de Tal",
        source: "Definido pelo próprio usuário no momento do cadastro.",
      },
      {
        name: "email",
        in: "body",
        type: "string",
        required: true,
        description: "E-mail que será utilizado para acesso ao sistema.",
        example: "fulano@example.com",
        source:
          "Informado pelo usuário no formulário de cadastro; será usado depois no login.",
      },
      {
        name: "senha",
        in: "body",
        type: "string",
        required: true,
        description: "Senha que o usuário utilizará no login.",
        example: "SenhaForte123",
        source: "Definida pelo próprio usuário no momento do cadastro.",
      },
      {
        name: "código",
        in: "body",
        type: "int",
        required: true,
        description:
          "Código fornecido para vinculo ao departamento principal. Ex: 142569 = Financeiro",
        example: "145634",
        source:
          "Fornecido por um usuário Administrador (rotatividade a cada 3 dias)",
      },
    ],
    playgroundDefaults: {
      body: {
        nome: "Fulano de Tal",
        email: "fulano@example.com",
        senha: "SenhaForte123",
        codigo: 12345,
      },
    },
    examples: [
      {
        title: "Exemplo de resposta (200)",
        response: {
          ok: true,
          usuario: {
            id: 46,
            nome: "Fulano de Tal",
            email: "fulano@example.com",
            departamento: "coordenacao",
            funcao: "pendente",
            empresa_id: "2",
            ativo: true,
            criado_em: "2025-11-20T02:05:54.085Z",
            picture: "null",
          },
          apiKey: "eva_live_rfA-Zg",
        },
      },
    ],
  },
];
